/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useRef, useEffect } from "react";
import {
  View,
  Text,
  ScrollView,
  TextInput,
  StyleSheet,
  Dimensions,
  Animated,
  KeyboardAvoidingView,
  Platform,
  TouchableOpacity,
} from "react-native";

import { useSelector } from "react-redux";
import { useSelectedLocation } from "../hooks";
import { RootState } from "../store";
import LHPulseHeader from "../components/LHPulseHeader";
import LHSectionHeader from "../components/LHSectionHeader";
import LHMiscellaneousObservations from "../components/LHMiscellaneousObservations.fixed.new";
import LHPdfExportBox from "../components/LHPdfExportBox";
import LHModalPop from "../components/LHModalPop";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { apiClient, createApiClient } from "../api/apiClient";
import PulseCheckActivity from "./PulseCheck/PulseCheckActivity";
import { useTheme } from "../theme";

const { width } = Dimensions.get("window");

interface PulseCheckScreenProps {
  onBackPress: () => void;
}

interface Question {
  ID: number;
  Question: string;
  Observation: string;
  SPARC_Category: string;
  Current_Category: string[];
}
const ShiftHandoverRedirectScreen: React.FC<PulseCheckScreenProps> = ({
  onBackPress,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkMode = resolvedTheme === "dark";
  // Get selected location for location-change detection
  const selectedLocation = useSelectedLocation();

  // Store initial location ID to detect changes after mount
  const [initialLocationId] = useState(selectedLocation?.id);

  // Detect location changes and redirect back to ShiftHandover Dashboard
  useEffect(() => {
    if (initialLocationId && selectedLocation?.id && initialLocationId !== selectedLocation.id) {
      onBackPress();
    }
  }, [selectedLocation?.id, initialLocationId, onBackPress]);

  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [formValues, setFormValues] = useState<Record<string, string>>({
    metric1: "",
    result1: "",
    target1: "",
    metric2: "",
    result2: "",
    goal: "",
  });
  const [notes, setNotes] = useState<{ first: string; second: string }>({
    first: "",
    second: "",
  });

  const [questionsData, setQuestionsData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [popup, setPopup] = useState<{
    visible: boolean;
    title: string;
    message: string;
  }>({
    visible: false,
    title: "",
    message: "",
  });
  
  // Get selected shift handover from Redux to determine if this is an existing handover
  const selectedShiftHandover = useSelector(
    (state: RootState) => state.shiftHandOver.selectedShiftHandOver
  );
  
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const api = createApiClient();
        
        // Only skip org context params for COMPLETED handovers
        // For new handovers or in-progress handovers, allow the interceptor to add org context params
        // This ensures:
        // - Completed handovers load their original question set (frozen at completion time)
        // - In-progress/new handovers get current questions filtered by location's org context
        const isCompletedHandover = selectedShiftHandover?.status?.toLowerCase() === 'completed';
        const response = await apiClient(
          api,
          "/shift-handover-walks/questions",
          "GET",
          undefined,
          isCompletedHandover ? { _skipOrgContextForQuestions: true } : undefined
        );
        if (response && response.data) {
          console.log("Response data:", response.data);
          // Set questionsData directly with the API response
          setQuestionsData(response.data);
          setError(null);
        } else {
          setError("Unable to fetch questions");
        }
      } catch (e) {
        setError("Unable to fetch questions");
      } finally {
        setLoading(false);
      }
    };
    fetchQuestions();
  }, [selectedShiftHandover]);

  //   if (loading) {
  //     return <LHLoader />;
  //   }

  //   if (error) {
  //     return (
  //       <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
  //         <Text style={{ color: "red", fontWeight: "bold" }}>{error}</Text>
  //       </View>
  //     );
  //   }
  const progress = useRef(new Animated.Value(0.4)).current;

  const toggleOption = (option: string): void => {
    setSelectedOptions((prev) =>
      prev.includes(option)
        ? prev.filter((o) => o !== option)
        : [...prev, option]
    );
  };

  const handleFormChange = (field: string, value: string): void => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = (): void => {
    const required = ["metric1", "result1", "target1", "metric2", "result2"];
    const emptyFields = required.filter((f) => !formValues[f]?.trim());
    if (emptyFields.length > 0) {
      setPopup({
        visible: true,
        title: "Validation Error",
        message: "Please fill all required fields.",
      });
    } else {
      setPopup({
        visible: true,
        title: "Success",
        message: "Form submitted successfully.",
      });
      Animated.timing(progress, {
        toValue: 1,
        duration: 500,
        useNativeDriver: false,
      }).start();
    }
  };

  const renderActivitySection = (sectionKey: string) => {
    console.log("questionsData[sectionKey]:", questionsData);
    if (
      !questionsData ||
      !questionsData[sectionKey] ||
      questionsData[sectionKey].length === 0
    ) {
      return null;
    }
    console.log("questionsData[sectionKey]:", questionsData);
    return (
      <PulseCheckActivity
        key={sectionKey}
        title={sectionKey}
        options={questionsData[sectionKey].map(
          (q: any) => q.Observation || q.Question
        )}
      />
    );
  };

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === "ios" ? "padding" : undefined}
      style={{ flex: 1, backgroundColor: colors.background.page }}
    >
      <ScrollView
        style={{ backgroundColor: colors.background.page }}
        contentContainerStyle={[
          styles.container,
          { backgroundColor: colors.background.page },
        ]}
      >
        <View style={styles.innerContainer}>
          <LHPulseHeader
            title="Shift Handover & Walk"
            subtitle="1st Shift with 01001336@pepsico.com"
            logoSource={require("../assets/warehouse_navigator.png")}
            onReturn={onBackPress}
          />
          <Text
            style={[
              styles.instructionBanner,
              isDarkMode && {
                backgroundColor: colors.background.surface,
                color: colors.text.primary,
                borderWidth: 1,
                borderColor: colors.border.default,
              },
            ]}
          >
            Please select any behaviors or observations you see below
          </Text>

          {renderActivitySection("Accuracy")}
          {renderActivitySection("Costs")}
          {renderActivitySection("People")}
          {renderActivitySection("Reliability")}
          {renderActivitySection("Safety")}

          <LHSectionHeader
            title="Miscellaneous Observations"
            textStyle={styles.header1text}
            icon={<MaterialIcons name="photo-camera" size={20} color={colors.text.inverse} />}
          />
          <LHMiscellaneousObservations
            stateKey="shiftHandOver"
            containerName="shifthandover"
          />

          <View
            style={[
              styles.actionRow,
              isDarkMode && { borderTopColor: colors.border.default },
            ]}
          >
            <View style={styles.pdfBoxWrapper}>
              <LHPdfExportBox containerStyle={{ width: "100%" }} />
            </View>
            <View style={styles.buttonColumn}>
              <TouchableOpacity
                style={[
                  styles.customButton,
                  styles.saveButton,
                  isDarkMode && { backgroundColor: colors.background.surface, borderColor: colors.border.default, borderWidth: 1 },
                ]}
                onPress={() =>
                  setPopup({
                    visible: true,
                    title: "Saved",
                    message: "Your Shift Handover progress has been saved.",
                  })
                }
              >
                <View style={styles.buttonContent}>
                  <MaterialIcons
                    name="save"
                    size={20}
                    color={isDarkMode ? colors.text.primary : "white"}
                    style={styles.icon}
                  />
                  <Text style={[styles.buttonText, isDarkMode && { color: colors.text.primary }]}>Save for later</Text>
                </View>
              </TouchableOpacity>

              <TouchableOpacity
                style={[
                  styles.customButton,
                  styles.completeButton,
                  isDarkMode && { backgroundColor: colors.brand.primary },
                ]}
                onPress={validateForm}
              >
                <View style={styles.buttonContent}>
                  <MaterialIcons
                    name="check-circle"
                    size={20}
                    color={colors.text.inverse}
                    style={styles.icon}
                  />
                  <Text style={[styles.buttonText, isDarkMode && { color: colors.text.inverse }]}>Complete</Text>
                </View>
              </TouchableOpacity>
            </View>
          </View>
        </View>

        {/* <Text style={styles.footer}>
          © 2025 PepsiCo | Pulse Check System v1.2
        </Text> */}
      </ScrollView>

      <LHModalPop
        visible={popup.visible}
        onClose={() => setPopup((prev) => ({ ...prev, visible: false }))}
        title={popup.title}
        description={popup.message}
        showFooter={true}
        primaryButtonText="OK"
        secondaryButtonText=""
        onPrimaryPress={() => setPopup((prev) => ({ ...prev, visible: false }))}
        showCloseButton={true}
      />
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: "#F5F7FA",
    paddingBottom: 50,
    flexGrow: 1,
    alignItems: "stretch",
    paddingLeft: 10,
    paddingRight: 10,
  },
  innerContainer: {
    // paddingHorizontal: Platform.OS === "web" ? "5%" : "0%",
  },
  welcomeCard: {
    backgroundColor: "white",
    borderRadius: 8,
    marginBottom: 16,
    borderLeftWidth: 4,
    borderLeftColor: "#0066CC",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  welcomeContainer: {
    flexDirection: "row",
    alignItems: "center",
    padding: 16,
    justifyContent: "space-between",
  },
  welcomeText: {
    fontSize: Platform.select({ web: 24, default: 20 }),
    fontWeight: "500",
    color: "#212529",
    marginLeft: 12,
  },
  returnButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#0066CC",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
  },
  returnButtonIcon: {
    color: "white",
    fontSize: 16,
    marginRight: 4,
  },
  returnButtonText: {
    color: "white",
    fontSize: 14,
    fontWeight: "500",
  },
  instructionBanner: {
    backgroundColor: "#004F9F",
    color: "white",
    textAlign: "center",
    padding: 12,
    borderRadius: 4,
    // marginBottom: 16,
  },
  optionGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: Platform.OS === "web" ? "flex-start" : "space-between",
    gap: Platform.OS === "web" ? 16 : 6,
    marginTop: 12,
    borderColor: "#e9ecef",
    backgroundColor: "white",
    borderWidth: 1,
    borderRadius: 6,
    padding: 16,
    //  width: Platform.OS === "web" ? "100%" : "100%",
  },
  notesInput: {
    borderWidth: 1,
    borderColor: "#ced4da",
    borderRadius: 6,
    marginTop: Platform.OS === "web" ? -20 : 30,
    padding: 12,
    minHeight: 100,
    color: "#868e96",
  },
  metricCardContainer: {
    borderWidth: 1,
    borderColor: "#ced4da",
    borderRadius: 6,
    padding: 10,
    // marginBottom: 8,
  },
  metricRowWrapper: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    gap: Platform.OS === "web" ? 16 : 12,
  },
  sectionHeader: {
    backgroundColor: "#FF6720",
  },
  header1text: {
    fontSize: 16,
  },
  footer: {
    textAlign: "center",
    marginTop: 40,
    color: "#868e96",
    fontSize: 12,
  },
  optionCardWrapper: {
    // width: Platform.OS === "web" ? "30%" : "100%",
    // marginBottom: Platform.OS === "web" ? 0 : 12,
  },
  actionRow: {
    flexDirection: Platform.OS === "web" ? "row" : "column",
    flexWrap: "wrap",
    marginTop: 30,
    borderTopWidth: 1,
    borderTopColor: "#dee2e6",
    paddingTop: 24,
    gap: 20,
  },
  pdfBoxWrapper: {
    flexBasis: Platform.OS === "web" ? "48%" : "100%",
    maxWidth: Platform.OS === "web" ? "48%" : "100%",
  },
  customButton: {
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 6,
    marginBottom: 12,
    width: "100%",
    backgroundColor: "#ccc",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  buttonContent: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  saveButton: {
    backgroundColor: "#FF9D2F",
  },
  completeButton: {
    backgroundColor: "#008242",
  },
  buttonText: {
    color: "white",
    fontSize: 16,
    fontWeight: "600",
  },
  icon: {
    marginRight: 8,
  },
  buttonColumn: {
    flexBasis: Platform.OS === "web" ? "48%" : "100%",
    maxWidth: Platform.OS === "web" ? "48%" : "100%",
    justifyContent: "center",
    marginRight: 5,
  },
  firstCheckHeader: {
    backgroundColor: "#1C7ED6",
  },
  secondCheckHeader: {
    backgroundColor: "#008080",
  },
  metGoalContainer: {
    width: Platform.OS === "web" ? "32%" : "100%",
    alignItems: "center",
  },
  metGoalText: {
    marginTop: 2,
    fontWeight: "bold",
    marginBottom: 2,
  },
  buttonTouch: {
    flexDirection: "row",
    width: "100%",
    backgroundColor: "#f0f0f0", // Default neutral background color
    paddingVertical: 10,
    paddingHorizontal: 0,
    borderRadius: 6,
    justifyContent: "space-between",
    alignItems: "center",
  },
  goalOption: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  goalTextOption: {
    fontWeight: "600",
  },
});
export default ShiftHandoverRedirectScreen;
